// Dashboard widget: the next scheduled calls, with today/tomorrow highlighted.
import React from 'react';
import styled from 'styled-components';
import { FaPhoneAlt, FaUser, FaExternalLinkAlt, FaClock } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import DashboardCard from './DashboardCard';
import formatPhone from '../shared/formatPhone';
import {
  formatEasternTime, relativeDayLabel, isToday, isTomorrow, statusMeta,
} from '../../modules/call-scheduling/utils/scheduleUtils';

const List = styled.div`
  display: flex; flex-direction: column; gap: ${props => props.theme.spacing(2)};
`;

const Item = styled.div`
  background-color: ${props => props.theme.colors.background};
  border-radius: ${props => props.theme.borderRadius};
  padding: ${props => props.theme.spacing(2)};
  cursor: pointer;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
  border-left: 4px solid ${props =>
    props.$today ? props.theme.colors.primary
      : props.$tomorrow ? props.theme.colors.success
      : props.theme.colors.inputGray};

  &:hover { transform: translateX(4px); box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1); }
`;

const Header = styled.div`
  display: flex; justify-content: space-between; align-items: center; gap: 8px;
  margin-bottom: ${props => props.theme.spacing(1)};
`;

const Name = styled.div`
  font-weight: ${props => props.theme.typography.fontWeight.medium};
  color: ${props => props.theme.colors.textPrimary};
  display: flex; align-items: center; gap: 8px;
`;

const When = styled.div`
  display: inline-flex; align-items: center; gap: 6px; font-size: ${props => props.theme.typography.fontSize.small};
  font-weight: ${props => (props.$soon ? props.theme.typography.fontWeight.medium : props.theme.typography.fontWeight.regular)};
  color: ${props =>
    props.$today ? props.theme.colors.primary
      : props.$tomorrow ? props.theme.colors.success
      : props.theme.colors.textSecondary};
`;

const Sub = styled.div`
  display: flex; align-items: center; justify-content: space-between; gap: 8px;
  font-size: ${props => props.theme.typography.fontSize.small};
  color: ${props => props.theme.colors.textSecondary};
`;

const Badge = styled.span`
  display: inline-flex; align-items: center; gap: 4px; padding: 2px 8px; border-radius: 10px;
  font-size: 0.68rem; font-weight: 700; background: ${p => p.$color}22; color: ${p => p.$color};
`;

const EmptyState = styled.div`
  text-align: center; padding: ${props => props.theme.spacing(4)};
  color: ${props => props.theme.colors.textSecondary};
`;

const UpcomingCalls = ({ data, loading, error }) => {
  const navigate = useNavigate();

  return (
    <DashboardCard
      title="Scheduled Calls"
      icon={<FaPhoneAlt />}
      viewAllLink="/bookings"
      loading={loading}
      error={error}
    >
      {data && data.length > 0 ? (
        <List>
          {data.map((call) => {
            const today = isToday(call.slot_start);
            const tomorrow = isTomorrow(call.slot_start);
            const meta = statusMeta(call.status);
            const name = call.name || call.lead?.name || 'Unknown';
            const phone = call.phone || call.lead?.phone;
            return (
              <Item
                key={call.id}
                $today={today}
                $tomorrow={tomorrow}
                onClick={() => navigate(`/bookings/${call.id}`)}
              >
                <Header>
                  <Name><FaUser size={12} /> {name}</Name>
                  <When $today={today} $tomorrow={tomorrow} $soon={today || tomorrow}>
                    <FaClock size={11} /> {relativeDayLabel(call.slot_start)} · {formatEasternTime(call.slot_start)}
                  </When>
                </Header>
                <Sub>
                  <span>{phone ? formatPhone(phone) : 'No phone'}</span>
                  <Badge $color={meta.color}>{meta.label}</Badge>
                </Sub>
              </Item>
            );
          })}
        </List>
      ) : (
        <EmptyState>No upcoming calls scheduled</EmptyState>
      )}
    </DashboardCard>
  );
};

export default UpcomingCalls;
